import { isPseudoLayerName }  from '@/TokamapEditor/helpers/planLayerMapping';
import type { PlanCategory }  from '@/TokamapEditor/helpers/planLayerMapping';
import type { DxfLayer }      from '@models/map/dxfTypes';
import type { PlanDecorLayer } from '@models/map/dxfTypes';
import type { PlanDecorStyle } from '@models/map/dxfTypes';

/**
 * Le décor du plan : les calques dessinés en fond, sous les POI (allées, fonds de scène,
 * barrières). Rien n'est plus reconnu par son nom côté serveur : c'est l'opérateur qui les
 * désigne, et son choix est proposé aux imports suivants du projet puis du compte.
 */

/** Calque => style ; un calque absent n'est pas du décor. */
export type PlanDecor = Record<string, PlanDecorStyle>;

export const DECOR_STYLE_LABELS: Record<PlanDecorStyle, string> = {
	allee: 'Allée',
	fond_scene: 'Fond de scène',
	barriere: 'Barrière',
};

export function buildDecor(suggested: PlanDecorLayer[]): PlanDecor {
	return Object.fromEntries(suggested.map(decor => [decor.layer, decor.style]));
}

/** Calques que prend une ligne cochée : ils partent en POI, pas en décor. */
function usedByCategories(categories: PlanCategory[]): Set<string> {
	return new Set(categories
		.filter(category => category.enabled)
		.flatMap(category => [category.baseLayer, category.numberLayer, category.briefLayer])
		.filter((name): name is string => !!name));
}

/**
 * Calques qu'on peut désigner « décor » : les calques NOMMÉS (un pseudo-calque de couleur n'est
 * pas retrouvé par le serveur au rendu du fond) que ne prend aucune ligne cochée. Ceux déjà en
 * décor d'abord.
 */
export function decorCandidates(layers: DxfLayer[], categories: PlanCategory[], decor: PlanDecor): DxfLayer[] {
	const used = usedByCategories(categories);

	return layers
		.filter(layer => !isPseudoLayerName(layer.name) && !used.has(layer.name))
		.sort((a, b) => (Number(b.name in decor) - Number(a.name in decor))
			|| a.name.localeCompare(b.name, undefined, { numeric: true }));
}

export function setDecorStyle(decor: PlanDecor, layer: string, style: PlanDecorStyle | null): PlanDecor {
	const next = { ...decor };
	if (null === style) delete next[layer];
	else next[layer] = style;

	return next;
}

/** Corps `decorLayers` de `POST /plan/mapping` : une ligne cochée l'emporte sur le décor. */
export function toDecorPayload(decor: PlanDecor, categories: PlanCategory[]): PlanDecorLayer[] {
	const used = usedByCategories(categories);

	return Object.entries(decor)
		.filter(([layer]) => !used.has(layer))
		.map(([layer, style]) => ({ layer, style }));
}
